// Hosting a game: pick the server (only asked when you're in more than one), then its town square
// and Houses category. Channels another live game is using are greyed out. Hosting while you still
// run a game in the same server asks first.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Volume2 } from "lucide-react";
import type { LiveGameSummary } from "@cc/shared";
import { api } from "../../lib/api.js";
import { useAuth } from "../../state/auth.js";
import { useGame } from "../../state/game.js";
import { Button, Dialog, DialogContent, Label } from "../ui/primitives.js";
import { GuildSelect } from "../ui/GuildSelect.js";
import { EndGameDialog } from "./LiveGames.js";

type ChannelOption = { id: string; name: string; inUseBy: string | null };
type DiscordChannels = { voiceChannels: ChannelOption[]; categories: ChannelOption[] };

const GUILD_KEY = "cc:host-guild";
const mainKey = (guildId: string) => `cc:main-channel:${guildId}`;
const housesKey = (guildId: string) => `cc:houses-category:${guildId}`;

/** The last server, channel and category you hosted with, so the next game is one click. */
function remembered(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* a convenience, not a requirement */
  }
}

const selectClass =
  "w-full rounded-md border border-ink-line bg-ink px-3 py-1.5 text-sm text-parchment focus:border-brass/60 focus:outline-none";

function ChannelSelect({
  id,
  label,
  noneLabel,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  noneLabel: string;
  options: ChannelOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
        <option value="">{noneLabel}</option>
        {options.map((c) => (
          <option key={c.id} value={c.id} disabled={c.inUseBy !== null}>
            {c.inUseBy ? `${c.name} — in use by game ${c.inUseBy}` : c.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function HostGame({ liveGames, onChanged }: { liveGames: LiveGameSummary[]; onChanged: () => void }) {
  const navigate = useNavigate();
  const guilds = useAuth((s) => s.guilds);
  const { createGame, joinGame, connected } = useGame();

  const hostable = guilds.filter((g) => g.canStoryteller);
  const [guildId, setGuildId] = useState(() => {
    const last = remembered(GUILD_KEY);
    return hostable.some((g) => g.id === last) ? last : (hostable[0]?.id ?? "");
  });
  const [channels, setChannels] = useState<DiscordChannels | null>(null);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [mainChannelId, setMainChannelId] = useState("");
  const [housesCategoryId, setHousesCategoryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"idle" | "confirmExisting" | "endExisting" | "confirmNoChannels">("idle");

  // Which channels are taken changes as games open and close, so reload when the live games do.
  const liveCodes = liveGames.map((g) => g.code).join(",");

  // The town square and Houses category are picked from the chosen server's own channels.
  useEffect(() => {
    if (!guildId) return;
    let cancelled = false;
    setChannelsError(null);
    api<DiscordChannels>(`/api/discord/channels?guildId=${encodeURIComponent(guildId)}`)
      .then((body) => {
        if (cancelled) return;
        setChannels(body);
        // A pick that has since been deleted or taken would sit invisibly selected.
        const usable = (list: ChannelOption[], id: string) => list.some((c) => c.id === id && !c.inUseBy);
        const pick = (list: ChannelOption[], current: string, key: string) =>
          usable(list, current) ? current : usable(list, remembered(key)) ? remembered(key) : "";
        setMainChannelId((current) => pick(body.voiceChannels, current, mainKey(guildId)));
        setHousesCategoryId((current) => pick(body.categories, current, housesKey(guildId)));
      })
      .catch((err: unknown) => {
        if (!cancelled) setChannelsError(err instanceof Error ? err.message : "Could not load Discord.");
      });
    return () => {
      cancelled = true;
    };
  }, [guildId, liveCodes]);

  const existing = liveGames.find((g) => g.role === "storyteller" && g.guildId === guildId);
  const noChannelsSelected = mainChannelId === "" && housesCategoryId === "";

  async function create(): Promise<void> {
    setStep("idle");
    setBusy(true);
    setError(null);
    const ack = await createGame({
      guildId,
      mainChannelId: mainChannelId || null,
      housesCategoryId: housesCategoryId || null,
    });
    remember(GUILD_KEY, guildId);
    remember(mainKey(guildId), mainChannelId);
    remember(housesKey(guildId), housesCategoryId);
    if (!ack.ok || !ack.code) {
      setBusy(false);
      setError(ack.ok ? "The server did not return a code." : ack.error);
      return;
    }
    const joined = await joinGame(ack.code, true);
    setBusy(false);
    if (!joined.ok) {
      setError(joined.error);
      return;
    }
    navigate(`/game/${ack.code}`);
  }

  /** Asks whichever confirmations apply, in order, then creates. */
  function proceed(from: "start" | "existing"): void {
    if (from === "start" && existing) return setStep("confirmExisting");
    if (noChannelsSelected) return setStep("confirmNoChannels");
    void create();
  }

  return (
    <section className="space-y-4 rounded-xl border border-brass/25 bg-ink-soft/75 p-5 shadow-2xl backdrop-blur-md">
      <div>
        <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-parchment">
          <Plus className="h-4 w-4 text-brass" aria-hidden="true" />
          Host a game
        </h2>
        <p className="mt-1 text-xs text-parchment-dim">
          Opens a fresh town square and gives you a code to share with your players.
        </p>
      </div>

      {guilds.length > 1 && (
        <div>
          <Label htmlFor="cc-guild">Discord server</Label>
          <GuildSelect id="cc-guild" guilds={guilds} value={guildId} onChange={setGuildId} />
        </div>
      )}

      {channelsError && <p className="text-xs text-evil">{channelsError}</p>}
      {channels && (
        <div className="space-y-3 rounded-md border border-ink-line bg-ink/50 p-3">
          <h3 className="flex items-center gap-2 text-sm font-medium text-parchment">
            <Volume2 className="h-4 w-4 shrink-0 text-brass" aria-hidden="true" />
            Discord Voice Settings
          </h3>
          <ChannelSelect
            id="cc-main-channel"
            label="Town square voice channel"
            noneLabel="None — don't move anyone"
            options={channels.voiceChannels}
            value={mainChannelId}
            onChange={setMainChannelId}
          />
          <ChannelSelect
            id="cc-houses-category"
            label="Houses category"
            noneLabel="None — don't create Houses"
            options={channels.categories}
            value={housesCategoryId}
            onChange={setHousesCategoryId}
          />
        </div>
      )}

      <Button
        variant="primary"
        className="w-full py-2.5 text-base"
        disabled={busy || !connected || !guildId}
        onClick={() => proceed("start")}
      >
        Start a new game
      </Button>
      {error && <p className="text-xs text-evil">{error}</p>}

      <Dialog open={step === "confirmExisting"} onOpenChange={(open) => !open && setStep("idle")}>
        <DialogContent title="You're already running a game here" className="w-[min(26rem,calc(100vw-2rem))]">
          <p className="text-sm text-parchment-dim">
            Game <span className="font-mono text-parchment">{existing?.code}</span> is still open in this server. End it
            first, or leave it running and start another.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setStep("idle")}>
              Close
            </Button>
            <Button onClick={() => proceed("existing")}>Continue anyway</Button>
            <Button variant="danger" onClick={() => setStep("endExisting")}>
              End {existing?.code}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EndGameDialog
        code={step === "endExisting" ? (existing?.code ?? null) : null}
        onClose={() => setStep("idle")}
        onEnded={() => {
          onChanged();
          proceed("existing");
        }}
      />

      <Dialog open={step === "confirmNoChannels"} onOpenChange={(open) => !open && setStep("idle")}>
        <DialogContent title="Start without channel management?" className="w-[min(24rem,calc(100vw-2rem))]">
          <p className="text-sm text-parchment-dim">
            No town square or Houses category is selected, so the bot won&apos;t move or mute anyone during this game.
            Players can still sign in and play.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setStep("idle")}>
              Close
            </Button>
            <Button variant="primary" disabled={busy || !connected} onClick={() => void create()}>
              Start anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
